import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { authApi } from "@/api/auth.api";
import { useAuthStore } from "@/store/auth.store";
import { connectSocket, disconnectSocket } from "@/sockets/socket";
import { toast } from "@/store/toast.store";
import type { LoginRequest } from "@/types/auth";

export function useAuthUser() {
  const user = useAuthStore((s) => s.user);
  const hasRole = useAuthStore((s) => s.hasRole);
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  return { user, hasRole, isAuthenticated };
}

export function useLogin() {
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: LoginRequest) => authApi.login(payload),
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      connectSocket();
      navigate("/dashboard", { replace: true });
    },
  });
}

export function useLogout() {
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authApi.logout(refreshToken),
    onSettled: () => {
      clear();
      disconnectSocket();
      navigate("/login", { replace: true });
    },
  });
}

export function useForgotPassword() {
  return useMutation({ mutationFn: (email: string) => authApi.forgotPassword(email) });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (payload: { email: string; code: string; newPassword: string }) => authApi.resetPassword(payload),
  });
}

/** Changing the password revokes every session server-side, so sign out locally and send the admin to /login. */
export function useChangePassword() {
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { currentPassword: string; newPassword: string }) => authApi.changePassword(payload),
    onSuccess: () => {
      clear();
      disconnectSocket();
      toast.success("Password changed. Please sign in again.");
      navigate("/login", { replace: true });
    },
  });
}
